/**
 * Hook for fetching filter schema data (property types + field types with operators).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchWithDefaults } from '@/lib/http/client';

import type { PropertyTypeSchema } from './usePropertySchema';

/**
 * Field type schema from backend API including supported operators.
 */
export interface FieldTypeSchema {
  field_type_name: string;
  description: string;
  value_type_name: string;
  operators: string[];
}

/**
 * Fetch field types with their supported operators.
 */
async function fetchFieldTypes(
  orgId: string | number
): Promise<FieldTypeSchema[]> {
  const url = `/api/orgs/${orgId}/crm-events/schemas/field-types/`;

  const response = await fetchWithDefaults(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch field types: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch property types for ALL entity types in one unfiltered request. Each
 * item carries `entity_type_name`, so callers group client-side. Prefer this
 * over per-type fan-out with `fetchPropertyTypes` when fetching for many
 * entity types at once — the schema endpoints share a per-org rate budget,
 * and a per-type fan-out can exhaust it (429s org-wide).
 */
export async function fetchAllPropertyTypes(
  orgId: string | number
): Promise<PropertyTypeSchema[]> {
  const url = `/api/orgs/${orgId}/crm-events/schemas/property-types/`;

  const response = await fetchWithDefaults(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch property types: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch property types for a entity type.
 */
export async function fetchPropertyTypes(
  orgId: string | number,
  entityTypeName: string
): Promise<PropertyTypeSchema[]> {
  const url = `/api/orgs/${orgId}/crm-events/schemas/property-types/?entity_type_name=${encodeURIComponent(entityTypeName)}`;

  const response = await fetchWithDefaults(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch property types: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Hook to fetch filter schema for advanced filtering.
 * Returns property types and field types separately with helper maps for efficient lookups.
 *
 * @param orgId - Organization ID
 * @param entityTypeName - Entity type name (e.g., 'ahoy_contact', 'ahoy_deal')
 * @returns Object containing filterable property types, field types, lookup maps, loading state, and error
 *
 * @example
 * ```tsx
 * const {
 *   filterablePropertyTypes,
 *   propertyTypeMap,
 *   fieldTypeMap,
 *   loading,
 *   error,
 *   retry
 * } = useFilterSchema(orgId, 'ahoy_contact');
 *
 * // filterablePropertyTypes: PropertyTypeSchema[] (only properties that can be filtered)
 * // Use fieldTypeMap to look up operators: fieldTypeMap[property.field_type_name].operators
 * ```
 */
export function useFilterSchema(
  orgId: string | number,
  entityTypeName: string
) {
  const [propertyTypes, setPropertyTypes] = useState<PropertyTypeSchema[]>([]);
  const [fieldTypes, setFieldTypes] = useState<FieldTypeSchema[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadSchema() {
      try {
        setLoading(true);
        setError(null);

        // Fetch both property types and field types in parallel
        const [propTypes, fldTypes] = await Promise.all([
          fetchPropertyTypes(orgId, entityTypeName),
          fetchFieldTypes(orgId),
        ]);

        if (cancelled) {
          return;
        }

        setPropertyTypes(propTypes);
        setFieldTypes(fldTypes);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error('Failed to fetch filter schema:', err);
        setError(err as Error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (orgId && entityTypeName) {
      loadSchema();
    }

    return () => {
      cancelled = true;
    };
  }, [orgId, entityTypeName, retryCount]);

  // Create helper maps for O(1) lookups
  const fieldTypeMap = useMemo<Record<string, FieldTypeSchema>>(() => {
    const map: Record<string, FieldTypeSchema> = {};
    for (const ft of fieldTypes) {
      map[ft.field_type_name] = ft;
    }
    return map;
  }, [fieldTypes]);

  const propertyTypeMap = useMemo<Record<string, PropertyTypeSchema>>(() => {
    const map: Record<string, PropertyTypeSchema> = {};
    for (const pt of propertyTypes) {
      map[pt.property_type_name] = pt;
    }
    return map;
  }, [propertyTypes]);

  // Filter properties that can be used in filters
  const filterablePropertyTypes = useMemo<PropertyTypeSchema[]>(() => {
    return propertyTypes
      .filter(prop => {
        // Exclude internal-only properties (default is 'all' when not specified)
        if (prop.value_read_access === 'internal') {
          return false;
        }

        // Exclude properties without filter support
        const fieldType = fieldTypeMap[prop.field_type_name];
        if (!fieldType || fieldType.operators.length === 0) {
          return false;
        }

        return true;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [propertyTypes, fieldTypeMap]);

  // Retry function for error recovery
  const retry = useCallback(() => {
    setRetryCount(prev => prev + 1);
  }, []);

  return {
    // Filtered property types ready for filtering
    filterablePropertyTypes,

    // Helper maps for O(1) lookups
    propertyTypeMap,
    fieldTypeMap,

    // Raw schemas (if needed)
    propertyTypes,
    fieldTypes,

    // State
    loading,
    error,
    retry,
  };
}
